/**
 * A live's planned moments, as the studio runs them and the room shows them.
 *
 * A moment is planned on the board "about N minutes in" and lands at that minute into the
 * live. The host can start one early, skip one before it lands, or close one that is up
 * (`live_moment_runs`, one row per session and moment). Everything here is pure: the studio,
 * the guest's page and the server all read a moment's place the same way from its minute,
 * its run and the seconds since the live began.
 */
import type { BoardArrive } from "./types";

export type MomentRunState = "started" | "skipped" | "closed";

/** What the host did with one moment in one session. No run: it follows its minute. */
export interface MomentRun {
  interactionId: string;
  state: MomentRunState;
  /** Unix seconds; set when the host started it early. */
  startedAt: number | null;
  /** Unix seconds; set when the host closed it. */
  closedAt: number | null;
}

/** One planned moment as the studio and the room need it. */
export interface PlannedMoment {
  id: string;
  /** Seconds into the live it lands. */
  appears_at: number;
  show?: BoardArrive;
}

/** What the host can do in the studio, and the words for it. */
export type MomentAction = "start" | "skip" | "close";

/** Where a moment stands in a session right now. */
export type MomentStanding =
  /** Still ahead: it lands at its minute unless the host starts or skips it. */
  | "planned"
  /** Up in the room now. */
  | "up"
  /** It was up and has had its time, or another moment took over. */
  | "shown"
  | "skipped"
  | "closed";

/** How long an unanswered moment holds the room, in seconds, or null for "until answered or the next one". */
export function liveStaySeconds(show: BoardArrive | undefined): number | null {
  if (!show) return null;
  if (show.stay === "15") return 15;
  if (show.stay === "45") return 45;
  return null;
}

/**
 * The second into the live a moment went up, or null when it has not (yet), or never will:
 * the host's early start, else its own minute once the live has reached it. A closed one
 * keeps the second it went up, so it still counts as the latest to have held the room.
 */
export function upAt(moment: PlannedMoment, run: MomentRun | undefined, into: number, wentLiveAt: number): number | null {
  if (run?.state === "skipped") return null;
  if (run?.startedAt != null) return Math.max(0, run.startedAt - wentLiveAt);
  return into >= moment.appears_at ? moment.appears_at : null;
}

/**
 * The one moment up in the room now, or null. Never a stack: the latest to go up holds the
 * room until the host closes it, or its stay runs out. One it took over from stays down.
 */
export function upNow(
  moments: readonly PlannedMoment[],
  runs: readonly MomentRun[],
  into: number,
  wentLiveAt: number,
): string | null {
  const byId = new Map(runs.map((r) => [r.interactionId, r]));
  let best: { moment: PlannedMoment; at: number } | null = null;
  for (const m of moments) {
    const at = upAt(m, byId.get(m.id), into, wentLiveAt);
    if (at === null) continue;
    if (!best || at > best.at || (at === best.at && m.id > best.moment.id)) best = { moment: m, at };
  }
  if (!best || byId.get(best.moment.id)?.state === "closed") return null;
  const stay = liveStaySeconds(best.moment.show);
  return stay !== null && into - best.at >= stay ? null : best.moment.id;
}

/** Where one moment stands in the session now, for the studio's list. */
export function standingOf(
  moment: PlannedMoment,
  moments: readonly PlannedMoment[],
  runs: readonly MomentRun[],
  into: number,
  wentLiveAt: number,
): MomentStanding {
  const run = runs.find((r) => r.interactionId === moment.id);
  if (run?.state === "skipped") return "skipped";
  if (run?.state === "closed") return "closed";
  if (upAt(moment, run, into, wentLiveAt) === null) return "planned";
  return upNow(moments, runs, into, wentLiveAt) === moment.id ? "up" : "shown";
}

/** The actions the studio offers for a moment that stands this way. */
export function actionsFor(standing: MomentStanding): MomentAction[] {
  if (standing === "planned") return ["start", "skip"];
  if (standing === "up") return ["close"];
  return [];
}

/** A run is the end of the moment for this session: it never shows, or no longer does. */
export function isOver(run: MomentRun | undefined): boolean {
  return run?.state === "skipped" || run?.state === "closed";
}
